// https://docs.datadoghq.com/real_user_monitoring/guide/send-custom-user-actions/
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { CONFIG } from '@frameio/config';
import { log } from '@frameio/utils/log';
import type { IdentifyOptions, TelemetryIdentity } from '../types';
import getRumOptions from './getRumOptions';

export { datadogRum as datadog } from '@datadog/browser-rum';

export const initDatadog = () => {
  const { datadogRum: datadogConfig, telemetryOptions } = CONFIG.thirdParty;
  if (!datadogConfig) return;

  try {
    const mergedOptions: RumInitConfiguration = {
      ...datadogConfig,
      ...(telemetryOptions?.enableDistributedTracing ? getRumOptions() : {}),
      version: CONFIG.releaseVersion,
    };

    datadogRum.init(mergedOptions);

    /**
     * The Session Replay does not start recording automatically when calling init(). Start the recording.
     * https://docs.datadoghq.com/real_user_monitoring/session_replay/#collect-session-replay-data
     */
    if (telemetryOptions?.enableSessionReplays)
      datadogRum.startSessionReplayRecording();
  } catch (e) {
    log.error(e);
  }
};

export function identify(
  identity: TelemetryIdentity,
  options?: IdentifyOptions
) {
  const userDetails: any = {
    id: identity.id,
  };

  if (identity.email) {
    userDetails.email = identity.email;
  }

  if (options?.buildInfo) {
    userDetails.buildInfo = options.buildInfo;
  }

  const accountId =
    identity.accountId ?? options?.identifyOptions?.accountId ?? undefined;
  if (accountId) {
    userDetails.accountId = accountId;
  }

  try {
    datadogRum.setUser(userDetails);
  } catch (e) {
    log.error(e);
  }
}

export const getDatadogRumSessionUrl = (): string => {
  let id: string | undefined = '';

  try {
    id = datadogRum.getInternalContext(Date.now())?.session_id;
  } catch (e) {
    log.error(e);
  }

  const startTime = Date.now() - 30000;

  return CONFIG.thirdParty.telemetryOptions?.enableSessionReplays && id
    ? `https://app.datadoghq.com/rum/replay/sessions/${id}?from=${startTime}`
    : '';
};
